import React, { useEffect, useState } from 'react';
import FormControl from '../../common/FormControl';
import { RCABucket } from '../../../services/capaRootCausesService';
import { userService, User } from '../../../services/userService';
import { filterPickableUsers } from '../../../utils/userPickable';
import sopInstanceService, { SopLite } from '../../../services/sopInstanceService';
import { capaTechItemsService, TechItem } from '../../../services/capaTechItemsService';
import { entityRegistryService } from '../../../services/entityRegistryService';

interface Props {
    bucket: RCABucket;
    itemTypeId: number | null;
    itemRecordId: number | null;
    onChange: (itemTypeId: number | null, itemRecordId: number | null) => void;
}

let _registryCache: Record<string, number> | null = null;
const loadRegistry = async (): Promise<Record<string, number>> => {
    if (_registryCache) return _registryCache;
    const types = await entityRegistryService.listTypes(true);
    _registryCache = Object.fromEntries(types.map(t => [t.type_code, t.id]));
    return _registryCache;
};

const RCAIdentifierPicker: React.FC<Props> = ({ bucket, itemTypeId, itemRecordId, onChange }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [processes, setProcesses] = useState<SopLite[]>([]);
    const [techItems, setTechItems] = useState<TechItem[]>([]);
    const [registry, setRegistry] = useState<Record<string, number>>({});

    useEffect(() => {
        loadRegistry().then(setRegistry).catch(() => setRegistry({}));
    }, []);

    useEffect(() => {
        if (bucket === 'People') {
            userService.getAllUsers({ limit: 200 }).then(r => setUsers(filterPickableUsers(r.data || []))).catch(() => {});
        } else if (bucket === 'Process') {
            sopInstanceService.listAllSops().then(setProcesses).catch(() => {});
        } else if (bucket === 'Technology') {
            capaTechItemsService.list().then(setTechItems).catch(() => {});
        }
    }, [bucket]);

    if (bucket === 'Environment') {
        return (
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>
                Environment bucket is text-only — the cause description above carries it.
            </div>
        );
    }

    if (bucket === 'People') {
        return (
            <FormControl.SearchSelect
                label="Identify Person (optional)"
                options={users.map(u => ({
                    value: u.id,
                    label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.login_id || `#${u.id}`,
                }))}
                value={itemRecordId}
                onChange={v => {
                    const id = v ? Number(v) : null;
                    onChange(id ? (registry['USER'] ?? null) : null, id);
                }}
                clearable
                data-testid="SEL_rca_identifier_person"
            />
        );
    }

    if (bucket === 'Process') {
        return (
            <FormControl.SearchSelect
                label="Identify SOP (optional)"
                options={processes.map(p => ({
                    value: p.id,
                    label: p.process_code ? `[${p.process_code}] ${p.process_name}` : p.process_name,
                }))}
                value={itemRecordId}
                onChange={v => {
                    const id = v ? Number(v) : null;
                    onChange(id ? (registry['PROCESS'] ?? null) : null, id);
                }}
                clearable
                data-testid="SEL_rca_identifier_process"
            />
        );
    }

    // Technology — combined dropdown with source badge (D6)
    return (
        <FormControl.SearchSelect
            label="Identify IT Application or Asset (optional)"
            options={techItems.map(t => ({
                value: `${t.source}:${t.id}`,
                label: `${t.source === 'APPLICATION' ? '[App]' : '[Asset]'} ${t.name}${t.code ? ` (${t.code})` : ''}`,
            }))}
            value={itemRecordId && itemTypeId ? `${(itemTypeId === registry['IT_ASSET'] ? 'IT_ASSET' : 'APPLICATION')}:${itemRecordId}` : null}
            onChange={v => {
                if (!v) { onChange(null, null); return; }
                const [source, idStr] = String(v).split(':');
                const id = Number(idStr);
                const typeId = source === 'IT_ASSET' ? registry['IT_ASSET'] : registry['APPLICATION'];
                onChange(typeId ?? null, id);
            }}
            clearable
            data-testid="SEL_rca_identifier_technology"
        />
    );
};

export default RCAIdentifierPicker;
